import Loading from 'components/common/Loading';
import { isPreviewBuild } from 'components/common/previewEnv';
import AuthGate from 'components/gates/AuthGate';
import TermsAcceptedGate from 'components/gates/TermsAcceptedGate';
import { elementType } from 'prop-types';
import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useMe } from 'store/app/auth/action';
import authUser from 'store/selectors/authUser';
import meStatus from 'store/selectors/meStatus';
import {
  getActiveCompanyId,
  mergeCompanyLists,
  pickDefaultCompany,
  resolveCompanyFromSubdomain,
  resolvePreferredCompanyId,
  syncActiveCompanyFromUrl,
} from 'utils/activeCompany';
import { getActiveAuthToken } from 'utils/getActiveAuthHeaders';
import getSubdomain from 'utils/getSubdomain';
import redirect from 'utils/redirect';

const HomeGate = ({ component }) => {
  const location = useLocation();
  const companies = useSelector(state => state.companies);
  const user = useSelector(authUser);
  const authUserId = useSelector(state => state.auth.user_id);
  const { isUninitialized, isFetching } = useSelector(meStatus);
  const {
    isLoading: isLoadingMe,
    isUninitialized: isUninitializedMe,
    needsCompanyRefetch,
  } = useMe();
  const tokenLastCompanyId = useSelector(state => state.auth.token_last_company_id);
  const storedCompanyId = useSelector(state => state.auth.company_id);
  const hasSession = useSelector(state => Boolean(getActiveAuthToken(state.auth)?.hash));
  const subdomain = getSubdomain();
  const preferredCompanyId = useMemo(
    () => resolvePreferredCompanyId(tokenLastCompanyId, storedCompanyId),
    [tokenLastCompanyId, storedCompanyId],
  );

  const companiesList = useMemo(
    () => mergeCompanyLists(companies, user?.companies),
    [companies, user?.companies],
  );

  const impersonationTargetId = useSelector(state => state.auth.impersonation?.targetUser?._id);
  const impersonationPending = useMemo(() => {
    if (!impersonationTargetId) {
      return false;
    }
    if (!user?._id && !authUserId) {
      return true;
    }
    const currentUserId = user?._id ?? authUserId;
    return String(currentUserId) !== String(impersonationTargetId);
  }, [authUserId, impersonationTargetId, user?._id]);

  const urlCompany = useMemo(() => {
    if (isPreviewBuild() || !subdomain) {
      return null;
    }
    return resolveCompanyFromSubdomain(companiesList, subdomain);
  }, [companiesList, subdomain]);

  const canRenderOnSubdomain = useMemo(() => {
    if (!subdomain || urlCompany) {
      return true;
    }
    const fallback = pickDefaultCompany(user, getActiveCompanyId());
    return fallback?.subDomain === subdomain;
  }, [subdomain, urlCompany, user]);

  const activeCompany = useMemo(() => {
    if (urlCompany) {
      return urlCompany;
    }
    if (!subdomain || !user?.companies?.length) {
      return null;
    }
    const fallback = pickDefaultCompany(user, getActiveCompanyId());
    return fallback?.subDomain === subdomain ? fallback : null;
  }, [subdomain, urlCompany, user]);

  useEffect(() => {
    if (
      !hasSession ||
      isPreviewBuild() ||
      isLoadingMe ||
      isUninitializedMe ||
      !subdomain ||
      !activeCompany
    ) {
      return;
    }
    syncActiveCompanyFromUrl(companiesList);
  }, [activeCompany, companiesList, hasSession, isLoadingMe, isUninitializedMe, subdomain]);

  useEffect(() => {
    if (!hasSession || isPreviewBuild() || isUninitialized || isLoadingMe || isUninitializedMe) {
      return;
    }
    if (!subdomain) {
      const defaultCompany = pickDefaultCompany(user, preferredCompanyId);
      if (defaultCompany?.subDomain) {
        redirect(
          location.pathname.startsWith('/home') ? location.pathname : '/home/messages',
          defaultCompany.subDomain,
        );
      }
      return;
    }
    if (urlCompany || activeCompany) {
      return;
    }
    const fallback = pickDefaultCompany(user, getActiveCompanyId());
    if (fallback?.subDomain && fallback.subDomain !== subdomain) {
      redirect('/home/messages', fallback.subDomain);
      return;
    }
    if (!isUninitialized) {
      redirect('/404');
    }
  }, [
    activeCompany,
    hasSession,
    isLoadingMe,
    isUninitialized,
    isUninitializedMe,
    location.pathname,
    preferredCompanyId,
    subdomain,
    urlCompany,
    user,
  ]);

  if (!hasSession) {
    return <AuthGate component={component} />;
  }

  if (
    isLoadingMe ||
    isUninitializedMe ||
    needsCompanyRefetch ||
    impersonationPending ||
    (impersonationTargetId && isFetching)
  ) {
    return <Loading />;
  }

  if (
    !isPreviewBuild() &&
    ((!subdomain && user?.companies?.length) || (subdomain && !canRenderOnSubdomain))
  ) {
    return null;
  }

  return <TermsAcceptedGate component={component} />;
};

HomeGate.propTypes = {
  component: elementType.isRequired,
};

export default memo(HomeGate);
